import axios from 'axios';
import { call, all, put, takeLatest, select, delay } from 'redux-saga/effects';

import history from '~/services/history';
import notification from '~/services/notification';
import {
  DELETE_ITEMS,
  GET_ALL_REQUEST,
  GET_BY_ID_REQUEST,
  GET_FILTER_PRODUCT_REQUEST,
  SAVE_REQUEST,
  GET_FILTER_SELECTS_REQUEST
} from './product-technical-constants';
import * as productTechnicalActions from './product-technical-actions';

import { ISA_ERROR } from '~/constants';

import { AppStateType } from '../root-reducer';

import { formatMessage } from '~/i18n/translate';
import domainService from '~/services/modules/domain/domain-service';
import productTechnicalService from './product-technical-service';

let cancel: any;
const CancelToken = axios.CancelToken;

const INITIAL_VALUES = {
  products: null,
  pictureUrl: ''
};

export function* getAll({ payload }: any) {
  if (cancel !== undefined) cancel();
  const cancelToken = new CancelToken(function executor(c) {
    cancel = c;
  });

  try {
    yield put(productTechnicalActions.setLoadingData(true));

    const { rowsPerPage, page, order, orderBy } = yield select((state: AppStateType) => state.productTechnical.list);

    const pagination = {
      skip: page === 0 ? 0 : page * rowsPerPage,
      limit: rowsPerPage,
      order,
      orderBy
    };

    const filters = {
      ...payload,
      sectionsExternalCodes: payload?.sectionsExternalCodes?.map(section => section.externalCode)?.join(',') ?? '',
      departmentExternalCodes:
        payload?.departmentExternalCodes?.map(department => department.externalCode)?.join(',') ?? '',
      providersExternalCodes:
        payload?.providersExternalCodes?.map(providers => providers.externalCode)?.join(',') ?? '',
      brandsExternalCodes: payload?.brandsExternalCodes?.map(brand => brand.externalCode)?.join(',') ?? '',
      description: payload?.description?.toLowerCase() ?? '',
      productsLinesExternalCodes:
        payload?.productLinesExternalCodes?.map(productLine => productLine.externalCode)?.join(',') ?? '',
      categoriesExternalCodes: payload?.categoriesExternalCodes?.map(category => category.externalCode)?.join(',') ?? ''
    };

    const [{ data: products }, { data: productsCount }] = yield all([
      call(
        productTechnicalService.getListProducts,
        {
          ...pagination,
          ...filters
        },
        {
          cancelToken
        }
      ),
      call(
        productTechnicalService.getListProductsCount,
        {
          ...filters
        },
        {
          cancelToken
        }
      )
    ]);

    yield put(
      productTechnicalActions.getAllSuccess({
        products,
        rowsCount: productsCount?.count
      })
    );
  } catch (error) {
    yield put({ type: ISA_ERROR, error });
  } finally {
    yield put(productTechnicalActions.setLoadingData(false));
  }
}

export function* getById({ id }: any) {
  yield put(productTechnicalActions.setLoadingData(true));

  try {
    if (cancel !== undefined) cancel();
    const cancelToken = new CancelToken(function executor(c) {
      cancel = c;
    });

    const { data: products } = yield call(
      productTechnicalService.getRawProducts,
      {
        fieldsToShow: 'externalCode,name',
        skip: 0,
        limit: 50
      },
      {
        cancelToken
      }
    );

    if (id) {
      yield put(
        productTechnicalActions.getByIdSuccess({
          data: {},
          selects: {
            products
          }
        })
      );
    } else {
      yield put(
        productTechnicalActions.getByIdSuccess({
          data: INITIAL_VALUES,
          selects: {
            products
          }
        })
      );
    }
  } catch (error) {
  } finally {
    yield put(productTechnicalActions.setLoadingData(false));
  }
}

export function* queryProducts({ queryText = '', skip }: any) {
  yield delay(200);

  if (cancel !== undefined) cancel();

  const cancelToken = new CancelToken(function executor(c) {
    cancel = c;
  });

  const { selects } = yield select((state: AppStateType) => state.productTechnical.form);

  try {
    yield put(productTechnicalActions.setLoadingSelects(true));
    const { data: products } = yield call(
      productTechnicalService.getRawProducts,
      {
        fieldsToShow: 'externalCode,name',
        skip,
        limit: 50,
        queryString: queryText
      },
      {
        cancelToken
      }
    );

    const existingProductIds = selects?.products?.map(product => product?.externalCode);
    const filteredProducts = products?.filter(product => !existingProductIds?.includes(product?.externalCode));
    const parsedProducts =
      [...selects?.products, ...filteredProducts]?.sort((a, b) => a?.name.localeCompare(b?.name)) ?? [];

    yield put(productTechnicalActions.getFilterProductSuccess(parsedProducts));
  } catch (error) {
    yield put({ type: ISA_ERROR, error });
  } finally {
    yield put(productTechnicalActions.setLoadingSelects(false));
  }
}

export function* saveTechnicalData({ payload, isBatch, id }: any) {
  try {
    yield put(productTechnicalActions.setLoadingSend(true));

    if (isBatch) {
      const formData = new FormData();
      formData.append('file', payload?.files[0]);

      yield call(productTechnicalService.saveZipTechnicalDataBatch, formData);
      yield put(productTechnicalActions.getAllRequest());
    } else {
      const productId = payload?.data?.products?._id;

      const formData = new FormData();
      formData.append('file', payload?.files[0]);

      yield call(productTechnicalService.savePdfTechnicalData, {
        payload: formData,
        productId
      });
    }

    yield call(
      notification.success,
      id
        ? formatMessage('productTechnicalDataFormEditSuccessMessage')
        : formatMessage('productTechnicalDataFormSaveSuccessMessage')
    );

    yield call(history.push, '/register/product-technical-data');
  } catch (error) {
    yield put({ type: ISA_ERROR, error });
  } finally {
    yield put(productTechnicalActions.setLoadingSend(false));
  }
}

export function* deleteItems({ payload }: any) {
  try {
    const { isConfirmed } = yield call(
      notification.confirm,
      formatMessage('commonLabelExclude'),
      formatMessage('productTechnicalDataFormDescriptionDelete')
    );

    if (isConfirmed) {
      yield put(productTechnicalActions.setLoadingSend(true));

      yield call(productTechnicalService.deleteItems, payload);

      yield put(productTechnicalActions.getAllRequest());
      yield call(notification.success, formatMessage('productTechnicalDataFormDeleteSuccessMessage'));
    }
  } catch (error) {
    yield put({ type: ISA_ERROR, error });
  } finally {
    yield put(productTechnicalActions.setLoadingSend(false));
  }
}

export function* getAllFilterSelects({ payload }: any) {
  try {
    yield put(productTechnicalActions.setLoadingSelects(true));

    const [
      { data: brand },
      { data: category },
      { data: department },
      { data: provider },
      { data: section },
      { data: productLines }
    ] = yield all([
      call(domainService.getBrands),
      call(domainService.getCategories),
      call(domainService.getDepartments),
      call(domainService.getProviders),
      call(domainService.getSections),
      call(domainService.getProductLines)
    ]);

    const selects = {
      brand,
      category,
      department,
      provider,
      section,
      productLines
    };

    yield put(productTechnicalActions.getAllSelectsSuccess(selects));
  } catch (error) {
    yield put({ type: ISA_ERROR, error });
  } finally {
    yield put(productTechnicalActions.setLoadingSelects(false));
  }
}

export default all([
  takeLatest(GET_ALL_REQUEST, getAll),
  takeLatest(GET_BY_ID_REQUEST, getById),
  takeLatest(GET_FILTER_PRODUCT_REQUEST, queryProducts),
  takeLatest(SAVE_REQUEST, saveTechnicalData),
  takeLatest(DELETE_ITEMS, deleteItems),
  takeLatest(GET_FILTER_SELECTS_REQUEST, getAllFilterSelects)
]);
